/* eslint-disable jsx-a11y/anchor-is-valid */
import { IonButtons, IonFab, IonFabButton, IonMenuButton, useIonModal, useIonViewDidEnter } from '@ionic/react';
import { FullEvent, FullPost } from 'model';
import { ChangeEvent, useCallback, useState } from 'react';
import { useHistory } from 'react-router';
import { Virtuoso } from 'react-virtuoso';
import { useCreatePostMutation } from '../api/PostApi';
import { EventCalendarItem } from '../components/EventCalendarItem';
import { Header } from '../components/Header';
import { IconButton } from '../components/IconButton';
import { IconAdd, IconCalendar, IconFilter } from '../components/QuirkIcons';
import { QuirkIonContent } from '../components/QuirkIonContent';
import { QuirkIonPage } from '../components/QuirkIonPage';
import { useEventFilters } from '../hooks/useEventFilters';
import { EventDateBadgeOutput, eventCalendarDateBadge } from '../util/eventCalendarDateBadge';
import { getEmptyPost } from '../util/getEmptyPost';
import { EventFilters } from './EventFilters';
// styles
import styles from './EventCalendar.module.css';
// eslint-disable-next-line css-modules/no-unused-class
import { NoResults } from '../components/NoResults';
import { QuirkLoader } from '../components/QuirkLoader';

const Divider: React.FC<{ label: string; topline?: boolean; empty?: boolean; createEvent?: () => void }> = ({
	label,
	topline,
	empty,
	createEvent
}) => {
	return (
		<div>
			<div className={styles.dividerContainer}>
				{topline && <div className={styles.topLine} />}
				<div className={styles.dividerLine} />
				<div className={styles.dividerLabel}>{label}</div>
				<div className={styles.dividerLine} />
			</div>
			{empty && (
				<div>
					<div className={styles.addButton}>
						<span>No Events.</span>
						<IconButton
							label="Create One"
							iconRight
							icon={
								<span className="iconCircle">
									<IconAdd />
								</span>
							}
							onClick={createEvent}
						/>
					</div>
				</div>
			)}
		</div>
	);
};

const EventItem: React.FC<{
	item: FullPost | string;
	lastItem: boolean;
	interested: number;
	eventDateBadge: EventDateBadgeOutput;
	createEvent: () => void;
}> = ({ item, interested, eventDateBadge, createEvent, lastItem }) => {
	if (typeof item === 'string') {
		return (
			<div key={item}>
				<Divider label={item} createEvent={createEvent} topline={true} empty={false} />
			</div>
		);
	}
	return (
		<div>
			{eventDateBadge &&
				eventDateBadge.previousMonths &&
				eventDateBadge.previousMonths.map((previousMonth, i) => {
					return (
						<div key={previousMonth + i}>
							<Divider label={previousMonth} createEvent={createEvent} topline={i === 0} empty />
						</div>
					);
				})}
			{eventDateBadge && eventDateBadge.newMonth && (
				<Divider label={eventDateBadge.newMonth} topline={eventDateBadge.previousMonths.length === 0} />
			)}
			<EventCalendarItem
				key={item._id}
				post={item}
				interested={interested}
				eventDateBadge={eventDateBadge}
				lastItem={lastItem}
			/>
		</div>
	);
};

export const EventCalendar = () => {
	const [userFilter, setUserFilter] = useState(false);
	const [myInterestedEventsFilter, setMyInterestedEventsFilter] = useState(false);
	const { filteredEvents, loadingEvents } = useEventFilters(userFilter, myInterestedEventsFilter);
	const history = useHistory();
	const processedDates: EventDateBadgeOutput[] = eventCalendarDateBadge(filteredEvents?.events as FullEvent[]);

	const [createPost] = useCreatePostMutation();

	const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
		e.stopPropagation();
		const { name, checked } = e.target;

		switch (name) {
			case 'myEvents':
				setUserFilter(checked);
				break;
			case 'interestedEvents':
				setMyInterestedEventsFilter(checked);
				break;
			default:
				break;
		}
	};
	useIonViewDidEnter(() => {
		setUserFilter(false);
		setMyInterestedEventsFilter(false);
	});
	const [presentReportFilter, dismissReportFilter] = useIonModal(EventFilters, {
		userFilter,
		myInterestedEventsFilter,
		handleCheckboxChange,
		onDismiss: () => dismissReportFilter()
	});

	const createEvent = useCallback(async () => {
		const result = await createPost({
			post: getEmptyPost('event')
		});
		if (!('error' in result)) {
			history.push(`/main/latest/event/edit/` + result.data._id);
		}
	}, [createPost, history]);

	return (
		<QuirkIonPage roles={['anonymous', 'user']} name="Event Calendar">
			<Header
				title="Event Calendar"
				left={
					<IonButtons>
						<IonMenuButton />
					</IonButtons>
				}
				right={
					<>
						<IconButton icon={<IconFilter />} onClick={presentReportFilter} />
					</>
				}
			/>

			<QuirkIonContent flush scroll={false}>
				<>
					<div className="ionFabContainer">
						<IonFab vertical="bottom" horizontal="end">
							<IonFabButton color={'secondary'} className="IonFabButton" onClick={createEvent}>
								<IconAdd size="xlarge" />
							</IonFabButton>
						</IonFab>
					</div>
					<>
						<QuirkLoader
							isLoading={loadingEvents}
							loadingMessage="Loading events..."
							data={processedDates.length > 0 ? filteredEvents : null}
							noResults={
								<NoResults
									heading="No events found."
									subheading="Create one now."
									icon={<IconCalendar />}
								/>
							}
						>
							<Virtuoso
								totalCount={filteredEvents?.events.length}
								data={[...(filteredEvents?.events || []), 'end']}
								className={styles.eventCalendarScroller}
								itemContent={(index, item) => (
									<>
										{item && (
											<div
												key={typeof item !== 'string' ? item?._id : item}
												style={{ minHeight: '1px' }}
											>
												<EventItem
													item={item}
													lastItem={
														!!(filteredEvents && index === filteredEvents.events.length - 1)
													}
													createEvent={createEvent}
													eventDateBadge={processedDates[index]}
													interested={Number(
														filteredEvents?.interest[index]
															? filteredEvents?.interest[index]
															: 0
													)}
												/>
											</div>
										)}
									</>
								)}
							/>
						</QuirkLoader>
					</>
				</>
			</QuirkIonContent>
		</QuirkIonPage>
	);
};
